import { useState } from "react";
import { useCreateCustomer } from "../hooks/useCustomer";
import { useNotification } from "@/shared/hooks/useNotification";
import { useApiError } from "@/shared/hooks/useApiError";
import { useFieldErrorActions } from "@/shared/contexts/field-error-context";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { queryClient } from "@/shared/services/queryClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { CustomerForm } from "./CustomerForm";

const FORM_ID = "createCustomer";

const initialFormData = {
  customerType: "Individual" as "Individual" | "Organization",
  customerName: "",
  idNumber: "",
  mobileNo1: "",
  mobileNo2: "",
  email: "",
  county: "",
  town: "",
  road: "",
  physicalLocationName: "",
  gpsLocation: "",
  customerGroupId: "",
  creditLimit: 0,
  paymentTerms: "",
};

export const CreateCustomerDialog = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  const createMutation = useCreateCustomer();
  const notify = useNotification();
  const confirm = useConfirm();
  const handleApiError = useApiError();
  const { clearForm, clearField } = useFieldErrorActions();

  const handleChange = (
    field: keyof typeof initialFormData,
    value: string | number,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearField(FORM_ID, field);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    clearForm(FORM_ID);

    confirm({
      title: "Create customer?",
      description: `“${formData.customerName}” will be added to the system.`,
      confirmLabel: "Create customer",
      onConfirm: () =>
        createMutation
          .mutateAsync({ ...formData, customerGroupId: formData.customerGroupId || null })
          .then(() => {
            notify.success("Customer created successfully");
            setFormData(initialFormData);
            setOpen(false);
            queryClient.invalidateQueries({ queryKey: ['customers'] });
          })
          .catch((error) => {
            handleApiError(error, { formId: FORM_ID });
            throw error;
          }),
    });
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) clearForm(FORM_ID);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Customer
        </Button>
      </DialogTrigger>
      <DialogContent
        
        className="max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]"
      >
        <DialogHeader>
          <DialogTitle>Create New Customer</DialogTitle>
          <DialogDescription>
            Add a new customer to the system
          </DialogDescription>
        </DialogHeader>

        <CustomerForm
          formId={FORM_ID}
          data={formData}
          onChange={handleChange}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending}
          submitButtonText="Create Customer"
        />
      </DialogContent>
    </Dialog>
  );
};
